import { makeAutoObservable, reaction, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { RouteType } from "types/RouteType";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import { isSessionEnded } from "stores/sessionGuard";
import { findOrThrow } from "shared/loadOrError";
import { LOG_PAGE, type DelegationLogEntry, type UserAdminDelegation } from "./delegation";

/**
 * Whether the signed-in person may open People: an administrator (who grants
 * and revokes people administration there), or a person an administrator made
 * a PEOPLE ADMINISTRATOR (who administers the people below them there).
 *
 * UI ONLY. Every People action is refused by the API unless its one shared
 * delegation guard admits it; this store only decides whether the page is
 * offered. It fails toward HIDDEN while unknown (like `requiresAdmin` in
 * pageVisibilityGate.ts): it only ever withholds a link the API would answer
 * with a refusal anyway.
 *
 * The delegate's own delegation row lives in the `user-admin-delegations`
 * ServiceStore (the API answers a non-administrator with their own row only).
 * An administrator's People page loads every row into that same store, so this
 * store never loads for an administrator — a narrower find would replace it.
 */
export class PeopleAccessStore {
  rootStore: RootStore;
  /** The own-delegation read has answered for the current person. */
  loaded = false;
  loadError: string | null = null;
  /** Settings → Account security: the record of delegated changes made TO this person. */
  ownChangesLoaded = false;
  ownChangesError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    registerPeopleAccess(this);
    reaction(
      () => this.currentUserId,
      (userId) => {
        runInAction(() => {
          this.loaded = false;
          this.loadError = null;
        });
        if (userId != null) void this.load();
      },
      { fireImmediately: true },
    );
  }

  get currentUserId(): number | null {
    const id = (this.rootStore as unknown as { authStore?: { currentUser?: { id?: unknown } } })
      .authStore?.currentUser?.id;
    const n = Number(id);
    return id == null || !Number.isInteger(n) ? null : n;
  }

  get isAdminUser(): boolean {
    return (this.rootStore as unknown as { authStore?: { isAdminUser?: boolean } }).authStore
      ?.isAdminUser === true;
  }

  private get service(): ServiceStoreType | null {
    return (
      (this.rootStore as unknown as { userAdminDelegationsStore?: ServiceStoreType })
        ?.userAdminDelegationsStore ?? null
    );
  }

  /** The signed-in person's own delegation, if they hold one (active or lapsed). */
  get ownDelegation(): UserAdminDelegation | null {
    const me = this.currentUserId;
    if (me == null) return null;
    const rows = (this.service?.entities ?? []) as unknown as UserAdminDelegation[];
    return rows.find((d) => Number(d.userId) === me) ?? null;
  }

  private get logService(): ServiceStoreType | null {
    return (
      (this.rootStore as unknown as { userAdminDelegationLogStore?: ServiceStoreType })
        ?.userAdminDelegationLogStore ?? null
    );
  }

  /**
   * The delegated changes made to the signed-in person's own account, newest
   * first. The API answers anyone with the rows that name them; the filter
   * here keeps a People page's wider read (an administrator's) out of it.
   */
  get ownChanges(): DelegationLogEntry[] {
    const me = this.currentUserId;
    if (me == null) return [];
    return ((this.logService?.entities ?? []) as unknown as DelegationLogEntry[])
      .filter((e) => e.targetUserId === me)
      .sort((a, b) => b.id - a.id);
  }

  /** Read them (Settings route hook). Never rejects; a failure is said, not shown as "none". */
  async loadOwnChanges(): Promise<void> {
    const me = this.currentUserId;
    const service = this.logService;
    if (me == null || !service) return;
    runInAction(() => {
      this.ownChangesError = null;
    });
    try {
      await findOrThrow(service, { query: { targetUserId: me, $limit: LOG_PAGE } });
      runInAction(() => {
        this.ownChangesLoaded = true;
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.ownChangesError = (err as { message?: string })?.message ?? "The changes to your account could not be loaded.";
      });
    }
  }

  /** Offer the People page? */
  get canOpenPeople(): boolean {
    return this.isAdminUser || !!this.ownDelegation;
  }

  /** Load the person's own delegation. Never rejects; a failure leaves the page hidden. */
  async load(): Promise<void> {
    if (this.isAdminUser) {
      runInAction(() => {
        this.loaded = true;
      });
      return;
    }
    const me = this.currentUserId;
    const service = this.service;
    if (me == null || !service) return;
    try {
      await service.find({ query: { userId: me } });
      runInAction(() => {
        this.loaded = true;
        this.loadError = null;
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.loadError = (err as { message?: string })?.message ?? "Could not check people administration.";
      });
    }
  }

  reset() {
    this.loaded = false;
    this.loadError = null;
    this.ownChangesLoaded = false;
    this.ownChangesError = null;
  }
}

let live: PeopleAccessStore | null = null;

/** Called from the store's constructor (route objects exist before any RootStore). */
export const registerPeopleAccess = (store: PeopleAccessStore | null): void => {
  live = store;
};

export const peopleAccess = (): PeopleAccessStore | null => live;

/**
 * The People route's gate: `hidden` is a GETTER over the access store (so the
 * OmniBar re-renders when the delegation loads — see billingGate.ts for why it
 * must stay a getter), and `beforeEnter` refuses a person who may not open it.
 */
export const gatePeopleRoute = (
  route: RouteType,
  resolve: () => PeopleAccessStore | null = peopleAccess,
): RouteType => ({
  ...route,
  get hidden(): boolean {
    if (route.hidden === true) return true;
    return resolve()?.canOpenPeople !== true;
  },
  beforeEnter: async (context) => {
    const store = resolve();
    if (!store) return false;
    if (!store.isAdminUser && !store.ownDelegation) await store.load();
    if (!store.canOpenPeople) {
      context.rootStore.notificationStore?.addNotification?.({
        message: "People is for administrators and for people an administrator lets manage accounts.",
        options: { variant: "warning" },
      });
      return false;
    }
    return route.beforeEnter ? route.beforeEnter(context) : true;
  },
});

export default PeopleAccessStore;
